import { safeFetchAPI } from '@/api';
import { log, logger, utils } from '@/helpers';
import { CampaignStatsByCampaign, ListCampaignStatsByCampaignRequest, PaginatedData, Pagination } from '@/types';
import { create } from 'zustand';
import { getCampaignReportFiltersStore, LOCAL_CAMPAIGN_REPORT_TEST_TOKEN, LOCAL_CAMPAIGN_REPORT_TEST_UID } from './createCampaignReportFiltersStore';
import { cloneDeep } from 'lodash';

export type ReportingCampaignListStoreState = {
	loaded: boolean;
	loading: boolean;
	campaigns: CampaignStatsByCampaign[];
} & Pagination;

export type ReportingCampaignListStoreActions = {
	fetchCampaignReportCampaignList: (req?: Partial<ListCampaignStatsByCampaignRequest>) => Promise<void>;
	nextPage: () => void;
	prevPage: () => void;
	reset: (filters?: Partial<ListCampaignStatsByCampaignRequest>) => void;
};

export type ReportingCampaignListStore = ReportingCampaignListStoreState & ReportingCampaignListStoreActions;

const initialState: ReportingCampaignListStoreState = {
	loaded: false,
	loading: false,
	page: 1,
	limit: 10,
	campaigns: [],
	totalResult: 0,
};

export const useCampaignReportCampaignListStore = create<ReportingCampaignListStore>((set, get) => ({
	...initialState,
	fetchCampaignReportCampaignList: async (req?: Partial<ListCampaignStatsByCampaignRequest>) => {
		const { page, limit } = get();
		const pagination = req?.pagination ?? { page, limit };

		set({ loading: true });

		const payload = {
			pagination,
			...req,
		};
		const [response, error] = await safeFetchAPI<PaginatedData<CampaignStatsByCampaign>>('/campaigns/:uid/reports/list', {
			method: 'POST',
			payload,
			forceToken: LOCAL_CAMPAIGN_REPORT_TEST_TOKEN,
			uid: LOCAL_CAMPAIGN_REPORT_TEST_UID,
		});

		if (error) {
			utils?.toaster?.addToast?.({
				message: error || 'Error loading campaigns',
				type: 'danger',
			});
			set({ loading: false, loaded: true });
			return;
		}

		logger.log('/campaigns/:uid/reports/list').prefix('global').addnl('response', response);

		set({
			...response?.pagination,
			loaded: true,
			loading: false,
			campaigns: cloneDeep(response?.data ?? []),
		});
	},
	nextPage: () => {
		const filters = getCampaignReportFiltersStore('global').getState().getReportFiltersFromRawFilters();
		get().fetchCampaignReportCampaignList({
			...filters,
			pagination: { page: get().page + 1, limit: get().limit },
		});
	},
	prevPage: () => {
		const filters = getCampaignReportFiltersStore('global').getState().getReportFiltersFromRawFilters();
		get().fetchCampaignReportCampaignList({
			...filters,
			pagination: { page: get().page - 1, limit: get().limit },
		});
	},
	reset: (filters?: Partial<ListCampaignStatsByCampaignRequest>) => {
		set({ ...initialState });
		if (filters) {
			get().fetchCampaignReportCampaignList({ ...filters });
		}
	},
}));
